/**
 * CADViewerPage - Main page for 3D CAD viewer prototype
 *
 * Layout: Left panel (upload + model list) | Center (3D viewer) | Right panel (metadata + AI analysis)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Boxes, Ruler } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import FeatureDisabled from '@components/common/FeatureDisabled';
import { CADMarkerDetailPanel } from '../components/cad/CADMarkerDetailPanel';
import CADClassificationDialog from '../components/cad/CADClassificationDialog';
import { useConfirmDiscardChat } from '../components/cad/viewer-page/useConfirmDiscardChat';
import {
  selectCadEnabled,
  selectCadThicknessEnabled,
} from '@features/featureFlags/featureFlagsSlice';
import { useAppSelector } from '@store/index';
import { selectUser } from '@features/auth/authSlice';
import { deleteCADModel, getGLBUrl, getModelGlbPrefix } from '../services/cad.service';
import { evictGlbsUnder } from '../services/cadGlbCache';
import { CADMarker, CADModel } from '../types/cad';
import { apiErrorStatus } from '../utils/apiErrorMessages';
import { useCadViewerModels } from './cad-viewer/useCadViewerModels';
import { useCadDrbfmOverlay } from './cad-viewer/useCadDrbfmOverlay';
import { useCadComparison } from './cad-viewer/useCadComparison';
import CadDeleteConfirmDialog from './cad-viewer/CadDeleteConfirmDialog';
import CadThicknessRoute from './cad-viewer/CadThicknessRoute';
import CadViewerHeader from './cad-viewer/CadViewerHeader';
import CadViewerBanners from './cad-viewer/CadViewerBanners';
import CadViewerSidebar from './cad-viewer/CadViewerSidebar';
import CadViewerStage from './cad-viewer/CadViewerStage';
import CadViewerInspectorPanel from './cad-viewer/CadViewerInspectorPanel';
import {
  canSwitchCadThicknessMode,
  toggleCadViewerModeParams,
} from '../utils/cadViewerMode';

const CADViewerPage: React.FC = () => {
  const { t } = useTranslation();
  const { setChatDirty, confirmDiscardChat } = useConfirmDiscardChat();
  const cadEnabled = useAppSelector(selectCadEnabled);
  const cadThicknessEnabled = useAppSelector(selectCadThicknessEnabled);
  const currentUser = useAppSelector(selectUser);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState(0);
  const [chatMarkers, setChatMarkers] = useState<CADMarker[]>([]);
  const [classificationTarget, setClassificationTarget] = useState<CADModel | null>(null);

  // URL params for DRBFM↔CAD linker (TG #673). Open `/cad-viewer?drbfm_session=<id>`
  // and (optionally) `&cad_model=<id>` to overlay failure-mode pins from a DRBFM run.
  const [searchParams, setSearchParams] = useSearchParams();
  const thicknessMode = searchParams.get('mode') === 'thickness';
  const drbfmSessionId = searchParams.get('drbfm_session');
  const cadModelParamId = searchParams.get('cad_model');
  const thicknessModelParamId = searchParams.get('model');
  const focusFailureModeId = searchParams.get('failure_mode');

  const {
    models,
    setModels,
    selectedModel,
    setSelectedModel,
    error,
    setError,
  } = useCadViewerModels({
    cadEnabled,
    cadThicknessEnabled,
    thicknessMode,
    thicknessModelParamId,
    cadModelParamId,
  });

  // The backend feature flag is already resolved against the authenticated
  // tenant allowlist. Reusing that result avoids a second, divergent frontend
  // tenant-code gate and keeps the control hidden for every disabled tenant.
  const showThicknessModeSwitch = canSwitchCadThicknessMode(cadThicknessEnabled);
  const handleToggleThicknessMode = useCallback(() => {
    setSearchParams((current) => toggleCadViewerModeParams(
      current,
      thicknessMode,
      selectedModel?.id,
    ));
  }, [selectedModel?.id, setSearchParams, thicknessMode]);

  const {
    drbfmMarkers,
    drbfmLoading,
    drbfmError,
    drbfmMarkersForCurrentModel,
    groupedDRBFMMarkersForCurrentModel,
    mergedMarkers,
    selectedMarker,
    setSelectedMarker,
  } = useCadDrbfmOverlay({
    drbfmSessionId,
    focusFailureModeId,
    selectedModel,
    chatMarkers,
  });

  // Comparison state (Issue #489 P0)
  const {
    comparisonModel,
    setComparisonModel,
    viewerMode,
    comparisonSummary,
    comparing,
    handleSetComparisonTarget,
    handleStartComparison,
    handleExitComparison,
  } = useCadComparison({
    selectedModel,
    setError,
    setRightTab,
    setChatMarkers,
  });

  // Reset markers on model change
  useEffect(() => { setChatMarkers([]); }, [selectedModel?.id]);

  // Handle new upload
  const handleUploadComplete = useCallback(
    (model: CADModel) => {
      setModels((prev) => [
        model,
        ...prev.filter((candidate) => candidate.id !== model.id),
      ]);
      setSelectedModel(model);
    },
    [setModels, setSelectedModel]
  );

  // Handle model selection. Switching models resets the ephemeral chat, so
  // guard the switch behind a discard confirmation when a draft is dirty.
  const handleSelect = useCallback(async (model: CADModel) => {
    if (!(await confirmDiscardChat())) return;
    setSelectedModel(model);
  }, [confirmDiscardChat, setSelectedModel]);

  // Handle delete
  const handleDeleteClick = useCallback((modelId: string) => {
    setDeleteTargetId(modelId);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetId) return;

    try {
      await deleteCADModel(deleteTargetId);
      evictGlbsUnder(getModelGlbPrefix(deleteTargetId));
      setModels((prev) => prev.filter((m) => m.id !== deleteTargetId));
      if (selectedModel?.id === deleteTargetId) {
        setSelectedModel(null);
      }
      // Clear comparison state if deleted model is the comparison target
      if (comparisonModel?.id === deleteTargetId) {
        handleExitComparison();
      }
    } catch (err: any) {
      // 404: already deleted server-side, so a later open must not be served its cached GLBs.
      if (apiErrorStatus(err) === 404) evictGlbsUnder(getModelGlbPrefix(deleteTargetId));
      setError(t('cad.deleteFailed'));
    }
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  }, [deleteTargetId, selectedModel, comparisonModel, handleExitComparison, setModels, setSelectedModel, setError, t]);

  const handleAnalysisComplete = useCallback((analysis: string) => {
    if (selectedModel) {
      setSelectedModel({ ...selectedModel, aiAnalysis: analysis });
      setModels((prev) =>
        prev.map((m) =>
          m.id === selectedModel.id ? { ...m, aiAnalysis: analysis } : m
        )
      );
    }
  }, [selectedModel, setSelectedModel, setModels]);

  const handleClassificationSaved = useCallback((updated: CADModel) => {
    setModels((current) => current.map((model) => (
      model.id === updated.id ? updated : model
    )));
    setSelectedModel((current) => current?.id === updated.id ? updated : current);
  }, [setModels, setSelectedModel]);

  const glbUrl = selectedModel?.status === 'completed' ? getGLBUrl(selectedModel.id) : null;
  const comparisonGlbUrl = comparisonModel?.status === 'completed' ? getGLBUrl(comparisonModel.id) : null;

  if (cadEnabled === null) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (cadEnabled === false) {
    return (
      <FeatureDisabled
        icon={<Boxes />}
        title={t('cad.disabledTitle')}
        description={t('cad.disabled')}
      />
    );
  }

  if (thicknessMode && cadThicknessEnabled === null) {
    return (
      <div className="flex h-[calc(100vh-64px)] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (thicknessMode && cadThicknessEnabled === false) {
    return (
      <FeatureDisabled
        icon={<Ruler />}
        title={t('cad.thickness.disabledTitle')}
        description={t('cad.thickness.disabled')}
      />
    );
  }

  if (thicknessMode) {
    return (
      <>
        <CadThicknessRoute
          models={models}
          selectedModel={selectedModel}
          currentUser={currentUser}
          error={error}
          onClearError={() => setError(null)}
          onSelectModel={handleSelect}
          onUploadComplete={handleUploadComplete}
          onDeleteModel={handleDeleteClick}
          onEditClassification={setClassificationTarget}
          deleteDialogOpen={deleteDialogOpen}
          onCloseDeleteDialog={() => setDeleteDialogOpen(false)}
          onConfirmDelete={handleDeleteConfirm}
          onExitThicknessMode={showThicknessModeSwitch
            ? handleToggleThicknessMode
            : undefined}
        />
        <CADClassificationDialog
          model={classificationTarget}
          onClose={() => setClassificationTarget(null)}
          onSaved={handleClassificationSaved}
        />
      </>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-4 p-4">
      <CadViewerHeader
        onOpenThicknessMode={showThicknessModeSwitch
          ? handleToggleThicknessMode
          : undefined}
      />

      <CadViewerBanners
        error={error}
        onDismissError={() => setError(null)}
        drbfmSessionId={drbfmSessionId}
        drbfmLoading={drbfmLoading}
        drbfmError={drbfmError}
        totalMarkers={drbfmMarkers.length}
        shownMarkers={drbfmMarkersForCurrentModel.length}
        locationCount={groupedDRBFMMarkersForCurrentModel.length}
      />

      {/* Main content: 3-column layout */}
      <div className="flex min-h-0 flex-1 gap-4">
        {/* Left panel: Upload + Model list */}
        <CadViewerSidebar
          models={models}
          selectedModel={selectedModel}
          comparisonModel={comparisonModel}
          viewerMode={viewerMode}
          comparing={comparing}
          onUploadComplete={handleUploadComplete}
          onSelect={handleSelect}
          onDelete={handleDeleteClick}
          onEditClassification={setClassificationTarget}
          onSetComparisonTarget={handleSetComparisonTarget}
          onClearComparisonTarget={() => setComparisonModel(null)}
          onStartComparison={handleStartComparison}
        />

        {/* Center: 3D Viewer(s) */}
        <CadViewerStage
          viewerMode={viewerMode}
          glbUrl={glbUrl}
          comparisonGlbUrl={comparisonGlbUrl}
          selectedModel={selectedModel}
          comparisonModel={comparisonModel}
          comparisonSummary={comparisonSummary}
          mergedMarkers={mergedMarkers}
          chatMarkers={chatMarkers}
          onMarkerClick={(marker) => {
            // Only DRBFM markers carry a back-pointer to a FailureMode and a
            // payload to render in the detail drawer. Chat markers have neither
            // — clicking them would otherwise pop an empty drawer.
            if (!marker.failureModeId || !marker.detailPayload) return;
            setSelectedMarker(marker);
          }}
          onExitComparison={handleExitComparison}
        />

        {/* Right panel: Tabs (Metadata / AI Analysis / Chat) */}
        <CadViewerInspectorPanel
          rightTab={rightTab}
          onTabChange={setRightTab}
          selectedModel={selectedModel}
          onAnalysisComplete={handleAnalysisComplete}
          onMarkersUpdate={setChatMarkers}
          onChatDirtyChange={setChatDirty}
          comparisonModelId={viewerMode === 'compare' ? comparisonModel?.id : undefined}
          comparisonModelName={viewerMode === 'compare' ? comparisonModel?.filename : undefined}
        />
      </div>

      {/* Delete confirmation dialog */}
      <CadDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      />

      <CADClassificationDialog
        model={classificationTarget}
        onClose={() => setClassificationTarget(null)}
        onSaved={handleClassificationSaved}
      />

      {/* DRBFM marker detail drawer (TG #673) */}
      <CADMarkerDetailPanel
        open={Boolean(selectedMarker)}
        marker={selectedMarker}
        onClose={() => setSelectedMarker(null)}
      />
    </div>
  );
};

export default CADViewerPage;
